/**
 * VCI-P1 Slice 3 — Internal SEO-Ready Venue Profile (Preview)
 * Route: /venue-preview/:slug
 *
 * Internal preview of the SEO-ready venue profile.
 * Phase 1: noindex, internal access only, "Internal Preview" banner visible.
 * Reuses canonical Venue 360° fields via the /api/venue-commerce/preview/by-slug/:slug endpoint.
 *
 * Features:
 *  - Responsive hero gallery with lightbox
 *  - Venue type, capacity, facilities grid
 *  - Indicative pricing with disclaimer
 *  - Venue spaces list
 *  - Structured data (JSON-LD) injected into document head
 *  - noindex meta injected into document head
 *  - CRM enquiry form (slide-over panel)
 *  - Content-completeness indicator (visible to supplier.manage/approve roles)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  ArrowLeft,
  Hotel,
  Users,
  Star,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  AlertCircle,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  X,
  Send,
  Loader2,
  Info,
  Shield,
  Tag,
  Zap,
  Coffee,
  Car,
  Home,
  Wifi,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import { Button }   from "../components/ui/button";
import { Badge }    from "../components/ui/badge";
import { Input }    from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast }    from "sonner";
import { useAuth }  from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Facility icon map ─────────────────────────────────────────────────────────
const FAC_META = {
  parking:              { icon: Car,     label: "Parking"           },
  ac_banquet:           { icon: Zap,     label: "AC Banquet"        },
  power_backup:         { icon: Zap,     label: "Power Backup"      },
  catering_inhouse:     { icon: Coffee,  label: "In-House Catering" },
  generator:            { icon: Zap,     label: "Generator"         },
  valet:                { icon: Car,     label: "Valet Parking"     },
  changing_rooms:       { icon: Home,    label: "Changing Rooms"    },
  guest_accommodation:  { icon: Hotel,   label: "Accommodation"     },
};

const VENUE_TYPE_LABELS = {
  GARDEN:          "Garden / Farmhouse",
  BANQUET_HALL:    "Banquet Hall",
  HOTEL:           "Hotel / Resort",
  RESORT:          "Resort",
  ROOFTOP:         "Rooftop Venue",
  BEACH:           "Beach Venue",
  DESTINATION:     "Destination Venue",
  CONFERENCE:      "Conference Centre",
  OTHER:           "Event Venue",
};

// ── Completeness ring ─────────────────────────────────────────────────────────
function CompletenessRing({ pct, ready }) {
  const r   = 20;
  const circ = 2 * Math.PI * r;
  const dash = circ * (pct / 100);
  return (
    <div className="flex items-center gap-2">
      <svg width="48" height="48" className="-rotate-90">
        <circle cx="24" cy="24" r={r} strokeWidth="4" className="fill-none stroke-gray-200" />
        <circle
          cx="24" cy="24" r={r} strokeWidth="4"
          className={`fill-none transition-all ${ready ? "stroke-emerald-500" : "stroke-amber-400"}`}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div>
        <p className="text-sm font-semibold text-gray-900">{pct}%</p>
        <p className={`text-xs ${ready ? "text-emerald-600" : "text-amber-600"}`}>
          {ready ? "Ready to publish" : "Needs content"}
        </p>
      </div>
    </div>
  );
}

// ── Gallery component ─────────────────────────────────────────────────────────
function Gallery({ images }) {
  const [active, setActive]     = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div
        data-testid="vci03-gallery-empty"
        className="w-full h-64 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 border-2 border-dashed"
      >
        <div className="text-center">
          <ImageIcon size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No gallery photos yet</p>
        </div>
      </div>
    );
  }

  const prev = () => setActive(i => (i - 1 + images.length) % images.length);
  const next = () => setActive(i => (i + 1) % images.length);

  return (
    <>
      {/* Main gallery */}
      <div className="relative rounded-xl overflow-hidden aspect-[16/9] bg-gray-100 group" data-testid="vci03-gallery">
        <img
          src={images[active]?.url}
          alt={images[active]?.label || "Venue photo"}
          className="w-full h-full object-cover cursor-pointer transition-opacity"
          onClick={() => setLightbox(true)}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={e => { e.stopPropagation(); prev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); next(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
        <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
          {active + 1} / {images.length}
        </div>
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          onClick={() => setLightbox(true)}
        >
          <div className="bg-black/30 rounded-full p-2">
            <Eye size={20} className="text-white" />
          </div>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                i === active ? "border-blue-500 opacity-100" : "border-transparent opacity-60 hover:opacity-80"
              }`}
            >
              <img src={img.url} alt={img.label || `Photo ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-white/20 rounded-full p-2 hover:bg-white/30"
            onClick={() => setLightbox(false)}
          >
            <X size={22} />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 rounded-full p-2"
            onClick={e => { e.stopPropagation(); prev(); }}
          >
            <ChevronLeft size={22} />
          </button>
          <img
            src={images[active]?.url}
            alt={images[active]?.label || "Venue photo"}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 rounded-full p-2"
            onClick={e => { e.stopPropagation(); next(); }}
          >
            <ChevronRight size={22} />
          </button>
        </div>
      )}
    </>
  );
}

// ── Enquiry form panel ────────────────────────────────────────────────────────
function EnquiryPanel({ slug, venueName, onClose }) {
  const [form, setForm] = useState({
    contact_name:        "",
    contact_email:       "",
    contact_phone:       "",
    event_type_interest: "",
    event_date:          "",
    guest_count_exact:   "",
    message:             "",
    consent_given:       false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.consent_given) {
      toast.error("Please accept the consent to submit your enquiry.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count_exact: form.guest_count_exact ? parseInt(form.guest_count_exact, 10) : undefined,
      };
      const res = await axios.post(
        `${API}/api/venue-commerce/preview/by-slug/${slug}/enquiry`,
        payload,
        { withCredentials: true },
      );
      setSubmitted(res.data);
      toast.success("Enquiry submitted successfully!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to submit enquiry. Please try again.";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="vci03-enquiry-panel">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-bold text-lg">Enquire About This Venue</h2>
              <p className="text-blue-100 text-sm mt-0.5">{venueName}</p>
            </div>
            <button onClick={onClose} className="text-blue-200 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h3 className="font-bold text-xl text-gray-900">Enquiry Submitted</h3>
            <p className="text-gray-500 text-sm">Your reference:</p>
            <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono text-blue-700" data-testid="vci03-enquiry-ref">
              {submitted.public_reference}
            </code>
            <p className="text-gray-400 text-xs mt-2">Lead {submitted.lead_number} created in CRM.</p>
            <Button onClick={onClose} variant="outline" className="mt-4">Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col p-6 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Your Name *</label>
              <Input
                value={form.contact_name}
                onChange={e => set("contact_name", e.target.value)}
                placeholder="Full name"
                required
                className="mt-1"
                data-testid="vci03-enquiry-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={e => set("contact_email", e.target.value)}
                placeholder="email@example.com"
                required
                className="mt-1"
                data-testid="vci03-enquiry-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone *</label>
              <Input
                type="tel"
                value={form.contact_phone}
                onChange={e => set("contact_phone", e.target.value)}
                placeholder="+91 98765 00000"
                required
                className="mt-1"
                data-testid="vci03-enquiry-phone"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Event Type</label>
                <select
                  value={form.event_type_interest}
                  onChange={e => set("event_type_interest", e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="vci03-enquiry-event-type"
                >
                  <option value="">Select</option>
                  <option value="WEDDING">Wedding</option>
                  <option value="CORPORATE">Corporate</option>
                  <option value="BIRTHDAY">Birthday</option>
                  <option value="ANNIVERSARY">Anniversary</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Event Date</label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={e => set("event_date", e.target.value)}
                  className="mt-1"
                  data-testid="vci03-enquiry-date"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Guest Count</label>
              <Input
                type="number"
                min="1"
                value={form.guest_count_exact}
                onChange={e => set("guest_count_exact", e.target.value)}
                placeholder="Approx. number of guests"
                className="mt-1"
                data-testid="vci03-enquiry-guest-count"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <Textarea
                value={form.message}
                onChange={e => set("message", e.target.value)}
                placeholder="Tell us about your event..."
                rows={3}
                className="mt-1 resize-none"
                data-testid="vci03-enquiry-message"
              />
            </div>
            <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
              <input
                type="checkbox"
                id="enquiry-consent"
                checked={form.consent_given}
                onChange={e => set("consent_given", e.target.checked)}
                className="mt-0.5"
                data-testid="vci03-enquiry-consent"
              />
              <label htmlFor="enquiry-consent" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                I consent to being contacted regarding my enquiry. My details will be handled per the privacy policy.
              </label>
            </div>
            <Button
              type="submit"
              disabled={submitting || !form.consent_given}
              className="w-full gap-2"
              data-testid="vci03-enquiry-submit"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Submitting…</>
              ) : (
                <><Send size={16} /> Send Enquiry</>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────
export default function VenuePublicProfile() {
  const { slug }      = useParams();
  const navigate      = useNavigate();
  const { user }      = useAuth();
  const canManage     = hasCapability(user, "supplier.manage");

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showEnquiry, setShowEnquiry] = useState(false);

  // ── SEO: inject noindex + structured data into document head ─────────────
  useEffect(() => {
    if (!data) return;

    // noindex for internal preview
    let noindexMeta = document.querySelector('meta[name="robots"][data-vci03]');
    if (!noindexMeta) {
      noindexMeta = document.createElement("meta");
      noindexMeta.setAttribute("name", "robots");
      noindexMeta.setAttribute("data-vci03", "true");
      document.head.appendChild(noindexMeta);
    }
    noindexMeta.setAttribute("content", "noindex, nofollow");

    // document title
    const profile = data.profile;
    const prevTitle = document.title;
    document.title = `${profile.venue_name || profile.display_name || "Venue"} — Internal Preview | Prashasta`;

    // JSON-LD structured data
    let ldScript = document.querySelector('script[type="application/ld+json"][data-vci03]');
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.setAttribute("type", "application/ld+json");
      ldScript.setAttribute("data-vci03", "true");
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify(data.structured_data || {});

    return () => {
      // cleanup on unmount
      noindexMeta?.remove();
      ldScript?.remove();
      document.title = prevTitle;
    };
  }, [data]);

  const fetchProfile = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/venue-commerce/preview/by-slug/${slug}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        setError({ type: "not_found", msg: `No venue found for slug "${slug}"` });
      } else if (status === 403) {
        setError({ type: "forbidden", msg: "You do not have permission to preview venue profiles." });
      } else {
        setError({ type: "error", msg: detail || "Failed to load venue profile." });
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="vci03-loading">
        <Loader2 size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4" data-testid="vci03-error">
        <AlertCircle size={48} className="text-amber-400" />
        <h2 className="text-2xl font-bold text-gray-800">
          {error.type === "not_found" ? "Venue Not Found" : "Preview Unavailable"}
        </h2>
        <p className="text-gray-500 max-w-sm">{error.msg}</p>
        <Button variant="outline" onClick={() => navigate("/venue-commerce/catalogue")} className="mt-4">
          <ArrowLeft size={16} className="mr-2" /> Back to Venue Catalogue
        </Button>
      </div>
    );
  }

  const { profile, completeness, banner } = data;
  const gallery    = (profile.media_gallery || []).filter(m => m.media_type === "IMAGE");
  const facilities = Object.entries(profile.facilities || {}).filter(([, v]) => v);
  const spaces     = profile.venue_spaces || [];
  const geo        = profile.geo_location || {};
  const eventSuits = profile.event_suitability || [];
  const priceFmt   = profile.pricing_indicative_from
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: profile.pricing_currency || "INR", maximumFractionDigits: 0 }).format(profile.pricing_indicative_from)
    : null;

  const maxCap = spaces.reduce((m, s) => Math.max(m, s.capacity_floating || s.capacity_seated || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="vci03-profile-page">

      {/* ── Internal Preview Banner ─────────────────────────────────────── */}
      <div
        className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4 sticky top-0 z-30"
        data-testid="vci03-internal-banner"
      >
        <div className="flex items-center gap-2 text-amber-800">
          <EyeOff size={14} className="flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider">Internal Preview</span>
          <span className="text-xs text-amber-600 hidden sm:inline">— Not indexed · Not publicly visible · Phase 1 gate active</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-xs ${profile.is_published ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-amber-400 text-amber-700 bg-amber-50"}`}
          >
            {profile.is_published ? "Published" : "Not Published"}
          </Badge>
          <Badge variant="outline" className="text-xs border-gray-300 text-gray-600 bg-white">
            {profile.verification?.status || "PENDING"}
          </Badge>
        </div>
      </div>

      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="vci03-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 space-y-8">

        {/* Gallery */}
        <Gallery images={gallery} />

        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2">
            <h1
              className="text-3xl font-bold text-gray-900"
              data-testid="vci03-venue-name"
            >
              {profile.venue_name || profile.display_name || profile.company_name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-gray-500 text-sm">
              {geo.city && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {[geo.locality, geo.city, geo.state].filter(Boolean).join(", ")}
                </span>
              )}
              {maxCap > 0 && (
                <span className="flex items-center gap-1">
                  <Users size={14} /> Up to {maxCap.toLocaleString()} guests
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {profile.venue_type && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs font-medium" data-testid="vci03-venue-type-badge">
                  <Hotel size={12} className="mr-1" />
                  {VENUE_TYPE_LABELS[profile.venue_type] || profile.venue_type}
                </Badge>
              )}
              {profile.managed_flag && (
                <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs font-medium">
                  <Shield size={12} className="mr-1" /> Managed
                </Badge>
              )}
              {eventSuits.map(s => (
                <Badge key={s} className="bg-gray-100 text-gray-700 border-gray-200 text-xs" data-testid={`vci03-suit-${s.toLowerCase()}`}>
                  {s.replace(/_/g, " ")}
                </Badge>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-2 min-w-[180px]">
            <Button
              size="lg"
              onClick={() => setShowEnquiry(true)}
              className="w-full gap-2 shadow-md"
              data-testid="vci03-enquiry-cta-btn"
            >
              <Send size={16} /> Enquire Now
            </Button>
            {priceFmt && (
              <p className="text-center text-xs text-gray-400">
                From {priceFmt}
                <span className="block text-gray-400">indicative pricing</span>
              </p>
            )}
          </div>
        </div>

        {/* ── Pricing section ──────────────────────────────────────────── */}
        {priceFmt && (
          <div
            className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid="vci03-pricing-section"
          >
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1">Indicative Pricing</p>
              <p className="text-2xl font-bold text-gray-900">{priceFmt} onwards</p>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3 max-w-xs text-xs text-amber-700">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              <p>Indicative only. Actual pricing depends on event type, guest count, dates and selected packages. Contact us for a customised quote.</p>
            </div>
          </div>
        )}

        {/* ── Facilities grid ───────────────────────────────────────────── */}
        {facilities.length > 0 && (
          <section data-testid="vci03-facilities-section">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Facilities & Amenities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {facilities.map(([key]) => {
                const meta = FAC_META[key] || { icon: CheckCircle, label: key };
                const Icon = meta.icon;
                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700"
                    data-testid={`vci03-fac-${key}`}
                  >
                    <Icon size={15} className="text-emerald-500 flex-shrink-0" />
                    {meta.label}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Venue spaces ──────────────────────────────────────────────── */}
        {spaces.length > 0 && (
          <section data-testid="vci03-spaces-section">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Spaces & Capacities</h2>
            <div className="space-y-3">
              {spaces.map((sp, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`vci03-space-${i}`}
                >
                  {sp.media_url && (
                    <img
                      src={sp.media_url}
                      alt={sp.name}
                      className="w-24 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{sp.name}</p>
                    {sp.space_type && <p className="text-xs text-gray-400 mt-0.5">{sp.space_type.replace(/_/g, " ")}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                      {sp.capacity_seated  && <span><Users size={12} className="inline mr-1" />Seated: {sp.capacity_seated}</span>}
                      {sp.capacity_floating && <span><Users size={12} className="inline mr-1" />Floating: {sp.capacity_floating}</span>}
                      {sp.dimensions_sqft  && <span>{sp.dimensions_sqft.toLocaleString()} sq ft</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── SEO description ───────────────────────────────────────────── */}
        {profile.seo_description && (
          <section data-testid="vci03-seo-description">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">About This Venue</h2>
            <p className="text-gray-600 leading-relaxed">{profile.seo_description}</p>
          </section>
        )}

        {/* ── Style tags ────────────────────────────────────────────────── */}
        {(profile.style_tags || []).length > 0 && (
          <section>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Style</h2>
            <div className="flex flex-wrap gap-2">
              {profile.style_tags.map(t => (
                <Badge key={t} variant="outline" className="text-xs text-gray-600 border-gray-300">
                  <Tag size={11} className="mr-1" />{t}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* ── Content completeness (editors only) ───────────────────────── */}
        {canManage && completeness && (
          <section
            className="bg-white rounded-xl border border-gray-200 p-5"
            data-testid="vci03-completeness-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Profile Completeness
              </h2>
              <CompletenessRing pct={completeness.pct} ready={completeness.ready_for_publish} />
            </div>
            {completeness.missing_fields.length > 0 && (
              <div className="space-y-1.5 mt-3">
                {completeness.missing_fields.map(f => (
                  <div key={f.field} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-1.5">
                    <AlertCircle size={12} className="flex-shrink-0" />
                    <span className="font-medium">{f.label}</span>
                    <span className="ml-auto text-amber-500">+{f.points} pts</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Bottom CTA ────────────────────────────────────────────────── */}
        <div
          className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4"
          data-testid="vci03-bottom-cta"
        >
          <div>
            <p className="font-bold text-lg">Interested in this venue?</p>
            <p className="text-blue-100 text-sm">Get in touch for availability and a personalised quote.</p>
          </div>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setShowEnquiry(true)}
            className="flex-shrink-0 gap-2 bg-white text-blue-700 hover:bg-blue-50"
            data-testid="vci03-bottom-enquiry-btn"
          >
            <Send size={16} /> Get a Quote
          </Button>
        </div>

        {/* ── Slug / internal reference ─────────────────────────────────── */}
        <div className="text-center text-xs text-gray-400">
          <code data-testid="vci03-slug-display">{profile.venue_slug}</code>
          {" · "}Profile ID: {slug}
        </div>

      </main>

      {/* ── Enquiry panel ──────────────────────────────────────────────── */}
      {showEnquiry && (
        <EnquiryPanel
          slug={slug}
          venueName={profile.venue_name || profile.company_name || ""}
          onClose={() => setShowEnquiry(false)}
        />
      )}
    </div>
  );
}
